import { useMutation } from "@apollo/client";
import { useEffect, useState } from "react";
import { GetAccountQuery } from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import {
  currentUserVar,
  errorNotificationVar,
  successNotificationVar,
} from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import ComboboxGeneric, {
  ComboboxGenericOption,
} from "@components/combobox/generic-combobox";
import SettingsLayout from "@components/settings-layout/settings-layout";
import ToggleSwitch from "@components/toggle-switch/toggle-switch";

import sendScheduledAiTestMutation from "../../graphql/send-scheduled-ai-test-mutation";
import updateUserMutation from "../../graphql/update-user-mutation";

const formatHourLabel = (hour: number) => {
  const period = hour < 12 ? "AM" : "PM";
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display}:00 ${period}`;
};

const hourOptions: ComboboxGenericOption<number>[] = Array.from(
  { length: 24 },
  (_, hour) => ({ value: hour, label: formatHourLabel(hour) }),
);

const leadTimeOptions: ComboboxGenericOption<number>[] = [5, 10, 15, 30].map(
  (minutes) => ({ value: minutes, label: `${minutes} minutes before` }),
);

const ScheduledAiSection = ({ me }: { me: GetAccountQuery["me"] }) => {
  const currentUser = currentUserVar();
  const userId = currentUser.id;
  const label = useLabel();

  const [dailyBriefEnabled, setDailyBriefEnabled] = useState<boolean>(true);
  const [dailyBriefHour, setDailyBriefHour] = useState<number>(9);
  const [meetingPrepEnabled, setMeetingPrepEnabled] = useState<boolean>(true);
  const [meetingPrepLeadTimeMinutes, setMeetingPrepLeadTimeMinutes] =
    useState<number>(10);
  const [meetingPrepOneononeOnly, setMeetingPrepOneononeOnly] =
    useState<boolean>(false);

  const [updateUser] = useMutation(updateUserMutation);
  const [sendTest] = useMutation(sendScheduledAiTestMutation);

  const [sendingKind, setSendingKind] = useState<string | null>(null);

  const hasSlackOrTeams = (me?.organizations?.edges ?? []).some(
    (edge) =>
      edge?.node?.hasSlackConnection || edge?.node?.hasTeamsInstallation,
  );

  const handleSendTest = async (kind: "daily_brief" | "meeting_prep") => {
    setSendingKind(kind);
    try {
      await sendTest({ variables: { kind } });
      successNotificationVar({
        title: "Test sent",
        description:
          "This can take 30 seconds to a minute. Check your Slack or Teams.",
      });
    } catch (e) {
      errorNotificationVar({
        title: "Could not send test",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setSendingKind(null);
    }
  };

  useEffect(() => {
    if (!me) return;
    setDailyBriefEnabled(me.dailyBriefEnabled ?? true);
    setDailyBriefHour(me.dailyBriefHour ?? 9);
    setMeetingPrepEnabled(me.meetingPrepEnabled ?? true);
    setMeetingPrepLeadTimeMinutes(me.meetingPrepLeadTimeMinutes ?? 10);
    setMeetingPrepOneononeOnly(me.meetingPrepOneononeOnly ?? false);
  }, [me]);

  const save = async (
    variables: Record<string, unknown>,
    rollback: () => void,
  ) => {
    try {
      await updateUser({ variables: { userId, ...variables } });
    } catch (e) {
      rollback();
      errorNotificationVar({
        title: "Could not save settings",
        description: e instanceof Error ? e.message : String(e),
      });
    }
  };

  const handleToggleDailyBrief = (next: boolean) => {
    const previous = dailyBriefEnabled;
    setDailyBriefEnabled(next);
    save({ dailyBriefEnabled: next }, () => setDailyBriefEnabled(previous));
  };

  const handleChangeDailyBriefHour = (
    option: ComboboxGenericOption<number>,
  ) => {
    if (!option) return;
    const previous = dailyBriefHour;
    setDailyBriefHour(option.value);
    save({ dailyBriefHour: option.value }, () => setDailyBriefHour(previous));
  };

  const handleToggleMeetingPrep = (next: boolean) => {
    const previous = meetingPrepEnabled;
    setMeetingPrepEnabled(next);
    save({ meetingPrepEnabled: next }, () => setMeetingPrepEnabled(previous));
  };

  const handleChangeLeadTime = (option: ComboboxGenericOption<number>) => {
    if (!option) return;
    const previous = meetingPrepLeadTimeMinutes;
    setMeetingPrepLeadTimeMinutes(option.value);
    save({ meetingPrepLeadTimeMinutes: option.value }, () =>
      setMeetingPrepLeadTimeMinutes(previous),
    );
  };

  const handleToggleMeetingPrepOneononeOnly = (next: boolean) => {
    const previous = meetingPrepOneononeOnly;
    setMeetingPrepOneononeOnly(next);
    save({ meetingPrepOneononeOnly: next }, () =>
      setMeetingPrepOneononeOnly(previous),
    );
  };

  return (
    <div aria-label="Scheduled AI section" className="pb-12">
      <h3 className="text-base font-medium text-gray-800 mb-4">Scheduled AI</h3>

      <SettingsLayout.Container>
        <SettingsLayout.Row>
          <SettingsLayout.Left label="A morning summary of your team's recent activity, sent to you in Slack.">
            Daily brief
          </SettingsLayout.Left>
          <SettingsLayout.Right>
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2">
                <ToggleSwitch
                  checked={dailyBriefEnabled}
                  onChange={handleToggleDailyBrief}
                  aria-label="Daily brief toggle"
                />
                <span className="text-sm text-gray-600">Enable</span>
              </div>
              {dailyBriefEnabled && (
                <div className="lg:w-72">
                  <ComboboxGeneric<number>
                    value={
                      hourOptions.find(
                        (option) => option.value === dailyBriefHour,
                      ) ??
                      hourOptions.find((option) => option.value === 9) ??
                      hourOptions[0]
                    }
                    options={hourOptions}
                    onChangeValue={handleChangeDailyBriefHour}
                    aria-label="Daily brief hour select"
                  />
                  <div className="text-gray-500 text-xs mt-1">
                    in your timezone
                  </div>
                </div>
              )}
              <div className="text-sm text-gray-600">
                Runs your{" "}
                <code className="bg-gray-100 px-1 rounded">/daily-brief</code>{" "}
                skill — edit that skill to customize what&apos;s included.
              </div>
              {hasSlackOrTeams && (
                <div>
                  <Button
                    theme={buttonTheme.lightGray}
                    size={ButtonSize.medium}
                    text="Send test"
                    aria-label="Send test daily brief"
                    disabled={sendingKind !== null}
                    onClick={() => handleSendTest("daily_brief")}
                  />
                </div>
              )}
            </div>
          </SettingsLayout.Right>
        </SettingsLayout.Row>

        <SettingsLayout.Row>
          <SettingsLayout.Left label="Context and suggested topics sent to you in Slack before your meetings.">
            Meeting prep
          </SettingsLayout.Left>
          <SettingsLayout.Right>
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2">
                <ToggleSwitch
                  checked={meetingPrepEnabled}
                  onChange={handleToggleMeetingPrep}
                  aria-label="Meeting prep toggle"
                />
                <span className="text-sm text-gray-600">Enable</span>
              </div>
              {meetingPrepEnabled && (
                <div className="lg:w-72">
                  <ComboboxGeneric<number>
                    value={
                      leadTimeOptions.find(
                        (option) => option.value === meetingPrepLeadTimeMinutes,
                      ) ??
                      leadTimeOptions.find((option) => option.value === 10) ??
                      leadTimeOptions[0]
                    }
                    options={leadTimeOptions}
                    onChangeValue={handleChangeLeadTime}
                    aria-label="Meeting prep lead time select"
                  />
                </div>
              )}
              {meetingPrepEnabled && (
                <div className="flex items-center gap-2">
                  <ToggleSwitch
                    checked={meetingPrepOneononeOnly}
                    onChange={handleToggleMeetingPrepOneononeOnly}
                    aria-label="Only 1-on-1s meeting prep"
                  />
                  <div className="text-sm text-gray-600">
                    {`Only ${label("oneonone", { pluralize: true })}`}
                  </div>
                </div>
              )}
              <div className="text-sm text-gray-600">
                Runs your{" "}
                <code className="bg-gray-100 px-1 rounded">/meeting-prep</code>{" "}
                skill — edit that skill to customize what&apos;s included.
              </div>
              {hasSlackOrTeams && (
                <div>
                  <Button
                    theme={buttonTheme.lightGray}
                    size={ButtonSize.medium}
                    text="Send test"
                    aria-label="Send test meeting prep"
                    disabled={sendingKind !== null}
                    onClick={() => handleSendTest("meeting_prep")}
                  />
                </div>
              )}
            </div>
          </SettingsLayout.Right>
        </SettingsLayout.Row>
      </SettingsLayout.Container>
    </div>
  );
};

export default ScheduledAiSection;
